import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
// MaintenanceRequestForm moved to TenantMessages

function TenantDashboard() {
  const { user, authReady } = useContext(AuthContext);
  const [, setLease] = useState(null);
  const [renewal, setRenewal] = useState(null);
  // chat moved to TenantMessages page
  
  const [ledgers, setLedgers] = useState([]);
  const [applications, setApplications] = useState([]);
  const [messageUnread, setMessageUnread] = useState(0);
  const [maintenanceUnread, setMaintenanceUnread] = useState(0);
  const [leaseUnread, setLeaseUnread] = useState(0);
  const [activeTab, setActiveTab] = useState('new-items');
  const [profileCompleteness, setProfileCompleteness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [paymentOnboarding, setPaymentOnboarding] = useState(null);
  const [paymentModeSaving, setPaymentModeSaving] = useState(false);
  const [viewingBookings, setViewingBookings] = useState([]);
  const [cancelBookingId, setCancelBookingId] = useState(null);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    address_line1: '',
    city: '',
    province: '',
    postal_code: '',
    country: '',
    date_of_birth: ''
  });
  const [profileStatus, setProfileStatus] = useState(null);
  const [profileError, setProfileError] = useState(null);
  const [profileSaving, setProfileSaving] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (!authReady) return;
    const fetchTenantData = async () => {
      if (!user || user.userType !== 'tenant') {
        navigate('/login');
        return;
      }

      const token = user.token;

      try {
        const [leaseRes, renewalRes, ledgerRes, applicationsRes, messagesRes, maintenanceRes, leaseCountRes, profileRes, completenessRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/leases/latest`, {
            headers: { 'Authorization': `Bearer ${token}` },
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/lease-renewals/latest`, {
            headers: { 'Authorization': `Bearer ${token}` },
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/rent-ledger`, {
            headers: { 'Authorization': `Bearer ${token}` },
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/applications`, {
            headers: { 'Authorization': `Bearer ${token}` },
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/messages/unread-count`, {
            headers: { 'Authorization': `Bearer ${token}` },
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/maintenance/unread-count`, {
            headers: { 'Authorization': `Bearer ${token}` },
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/lease/unread-count`, {
            headers: { 'Authorization': `Bearer ${token}` },
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/profile`, {
            headers: { 'Authorization': `Bearer ${token}` },
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/profile/completeness`, {
            headers: { 'Authorization': `Bearer ${token}` },
          })
        ]);
        const leaseData = leaseRes.ok ? await leaseRes.json() : { lease: null };
        const renewalData = renewalRes.ok ? await renewalRes.json() : { renewal: null };
        const ledgerData = ledgerRes.ok ? await ledgerRes.json() : { ledgers: [] };

        const applicationsData = applicationsRes.ok ? await applicationsRes.json() : { applications: [] };
        const messagesData = messagesRes.ok ? await messagesRes.json().catch(() => ({})) : {};
        const maintenanceData = maintenanceRes.ok ? await maintenanceRes.json().catch(() => ({})) : {};
        const leaseCountData = leaseCountRes.ok ? await leaseCountRes.json().catch(() => ({})) : {};
        const profileData = profileRes.ok ? await profileRes.json().catch(() => ({})) : {};
        if (profileRes.ok && profileData.profile) {
          setProfile(profileData.profile);
        }
        const completenessData = completenessRes?.ok ? await completenessRes.json().catch(() => ({})) : {};
        if (completenessData.completeness !== undefined) setProfileCompleteness(completenessData);
        setLease(leaseData.lease || null);
        setRenewal(renewalData.renewal || null);
        setLedgers(ledgerData.ledgers || []);

        setApplications(applicationsData.applications || []);
        setMessageUnread(messagesData.count || 0);
        setMaintenanceUnread(maintenanceData.count || 0);
        setLeaseUnread(leaseCountData.count || 0);

        // Fetch onboarding progress, viewing bookings, recently viewed, payment onboarding
        try {
          const [onbRes, vbRes, rvRes, poRes] = await Promise.all([
            fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/onboarding`, {
              headers: { 'Authorization': `Bearer ${token}` }
            }),
            fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/viewings`, {
              headers: { 'Authorization': `Bearer ${token}` }
            }),
            fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/recently-viewed`, {
              headers: { 'Authorization': `Bearer ${token}` }
            }),
            fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/payment-onboarding`, {
              headers: { 'Authorization': `Bearer ${token}` }
            })
          ]);
          if (onbRes.ok) {
            const onbData = await onbRes.json();
            setOnboarding(onbData.steps || null);
          }
          if (vbRes.ok) {
            const vbData = await vbRes.json();
            setViewingBookings(vbData.viewings || []);
          }
          if (rvRes.ok) {
            const rvData = await rvRes.json();
            setRecentlyViewed(rvData.properties || []);
          }
          if (poRes.ok) {
            const poData = await poRes.json();
            setPaymentOnboarding(poData);
          }
        } catch (fetchErr) {
          console.error('Onboarding/viewings/recently-viewed fetch error:', fetchErr.message);
        }

      } catch (err) {
        setError(err.message);
        console.error("Error fetching tenant data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTenantData();
  }, [user, navigate, authReady]);

  useEffect(() => {
    if (location.hash === '#viewings') {
      setActiveTab('viewings');
    }
  }, [location.hash]);

  // chat moved to TenantMessages page

  if (!authReady) {
    return <main className="App-main">Loading dashboard...</main>;
  }
  if (loading) {
    return <main className="App-main">Loading your dashboard...</main>;
  }

  // chat moved to TenantMessages page

  if (error) {
    return <main className="App-main"><p className="error-message">Error: {error}</p></main>;
  }

  const todayIso = new Date().toISOString().slice(0, 10);
  const nextDueEntry = ledgers
    .filter(entry => entry.status !== 'paid' && entry.due_date && entry.due_date >= todayIso)
    .sort((a, b) => a.due_date.localeCompare(b.due_date))[0];
  const visibleDue = ledgers.filter(entry => {
    if (entry.status === 'paid') return false;
    if (entry.due_date && entry.due_date < todayIso) return true;
    if (nextDueEntry && entry.id === nextDueEntry.id) return true;
    return false;
  });

  const validatePostal = (value, countryValue) => {
    if (!countryValue || !value) return true;
    const normalizedCountry = countryValue.trim().toLowerCase();
    if (normalizedCountry === 'canada' || normalizedCountry === 'ca') {
      return /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(value.trim());
    }
    if (normalizedCountry === 'united states' || normalizedCountry === 'usa' || normalizedCountry === 'us') {
      return /^\d{5}(-\d{4})?$/.test(value.trim());
    }
    return true;
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const saveProfile = async () => {
    setProfileError(null);
    setProfileStatus(null);
    if (!validatePostal(profile.postal_code, profile.country)) {
      setProfileError('Please enter a valid postal/ZIP code for the selected country.');
      return;
    }
    const token = user?.token;
    if (!token) return;
    setProfileSaving(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(profile)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Unable to update profile');
      setProfile(data.profile || profile);
      setProfileStatus('Profile saved.');
    } catch (err) {
      setProfileError(err.message || 'Unable to update profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const now = new Date();
  const applicationCount = applications.length;
  const upcomingPayments = visibleDue.length;
  const upcomingBookings = viewingBookings
    .filter((vb) => vb.status === 'confirmed' && new Date(vb.start_time) >= now)
    .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
  const upcomingViewingsCount = upcomingBookings.length;

  const tenantSummaryItems = [
    { id: 'applications', label: 'Applications', count: applicationCount, link: '/tenant/applications' },
    { id: 'lease', label: 'Lease updates', count: leaseUnread, link: '/tenant/lease' },
    { id: 'viewings', label: 'Upcoming viewings', count: upcomingViewingsCount, link: '/tenant/dashboard#viewings' },
    { id: 'messages', label: 'Unread messages', count: messageUnread, link: '/tenant/messages' },
    { id: 'maintenance', label: 'Maintenance updates', count: maintenanceUnread, link: '/tenant/maintenance' },
    { id: 'payments', label: 'Payments due', count: upcomingPayments, link: '/tenant/payments' }
  ];

  return (
    <main className="App-main tenant-dashboard-page">
      <div className="ledger-header">
        <div>
          <p className="eyebrow-tag">Tenant Dashboard</p>
          <h2>Tenant Dashboard</h2>
          <p>Track requests, lease documents, and rent status in one place.</p>
        </div>
      </div>

      {profileCompleteness && profileCompleteness.completeness < 100 && (
        <div className="dashboard-section" style={{ borderLeft: '4px solid #f59e0b', background: '#fffbeb', padding: '0.75rem', marginBottom: '0.75rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem' }}>Complete your profile — {profileCompleteness.completeness}%</h3>
              <p className="status-text" style={{ margin: '0.25rem 0 0' }}>
                Missing: {profileCompleteness.missing?.join(', ')}
              </p>
            </div>
            <a href="/tenant/profile" className="secondary-button" style={{ whiteSpace: 'nowrap' }}>Update Profile</a>
          </div>
          <div style={{ background: '#e5e7eb', borderRadius: '4px', height: '6px', marginTop: '0.5rem', overflow: 'hidden' }}>
            <div style={{ background: '#f59e0b', height: '100%', width: `${profileCompleteness.completeness}%`, borderRadius: '4px', transition: 'width 0.3s' }} />
          </div>
        </div>
      )}

      {paymentOnboarding?.needsSetup && (
        <div className="dashboard-section" style={{ borderLeft: '4px solid #2f6fed', background: '#f5f8ff', padding: '0.75rem', marginBottom: '0.75rem' }}>
          <h3 style={{ margin: '0 0 0.35rem' }}>🏠 Set up your rent payments</h3>
          <p className="status-text" style={{ margin: '0 0 0.65rem' }}>
            Your lease is signed. Choose how you'd like to pay rent.
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <Link to="/tenant/payments?tab=payment-methods" className="primary-button" style={{ textDecoration: 'none' }}>
              Set up auto-pay →
            </Link>
            <button
              className="secondary-button"
              disabled={paymentModeSaving}
              onClick={async () => {
                setPaymentModeSaving(true);
                try {
                  const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/payment-mode`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user.token}` },
                    body: JSON.stringify({ mode: 'off_platform' })
                  });
                  if (res.ok) {
                    setPaymentOnboarding(prev => ({ ...prev, needsSetup: false, paymentMode: 'off_platform' }));
                  }
                } catch (err) {
                  console.error('Payment mode error:', err.message);
                } finally {
                  setPaymentModeSaving(false);
                }
              }}
            >
              {paymentModeSaving ? 'Saving…' : 'I pay my landlord directly'}
            </button>
          </div>
        </div>
      )}

      {!loading && onboarding && !(onboarding.profile_complete && onboarding.viewed_listing && onboarding.has_applied) && (
        <div className="dashboard-section" style={{ borderLeft: '4px solid var(--accent-color, #2f6fed)', padding: '0.75rem', marginBottom: '0.75rem' }}>
          <h3 style={{ margin: '0 0 0.65rem' }}>Getting started</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {[
              { key: 'profile_complete', label: 'Complete your profile', link: '/tenant/profile', cta: 'Update profile' },
              { key: 'viewed_listing', label: 'View a listing', link: '/properties', cta: 'Browse listings' },
              { key: 'has_applied', label: 'Apply to a property', link: '/properties', cta: 'Find a place' },
            ].map((step, i) => {
              const done = onboarding[step.key];
              return (
                <div key={step.key} style={{ padding: '0.35rem 0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '0.9rem', width: '1.5rem', textAlign: 'center' }}>
                      {done ? '✅' : <span style={{ color: 'var(--light-text-color)', fontWeight: 600 }}>{i + 1}.</span>}
                    </span>
                    {done ? (
                      <span style={{ color: 'var(--light-text-color)', textDecoration: 'line-through', fontSize: '0.9rem' }}>{step.label}</span>
                    ) : (
                      <span style={{ color: 'var(--text-color)', fontSize: '0.9rem', fontWeight: 500 }}>{step.label}</span>
                    )}
                  </div>
                  {!done && (
                    <div style={{ marginLeft: '2rem', marginTop: '0.3rem' }}>
                      <Link to={step.link} className="secondary-button" style={{ fontSize: '0.78rem', padding: '0.25rem 0.6rem', textDecoration: 'none' }}>
                        {step.cta}
                      </Link>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: 'var(--light-text-color)' }}>
            {[onboarding.profile_complete, onboarding.viewed_listing, onboarding.has_applied].filter(Boolean).length} of 3 complete
          </div>
        </div>
      )}

      {renewal && renewal.status === 'sent' && (
        <div className="dashboard-section" style={{ borderLeft: '4px solid #2f6fed', background: '#f5f8ff', padding: '0.75rem', marginBottom: '0.75rem' }}>
          <div className="section-header" style={{ alignItems: 'center' }}>
            <h3 style={{ margin: 0 }}>📋 Lease Renewal Offer</h3>
            <span className="status-pill sent" style={{ background: '#2f6fed', color: '#fff' }}>Action required</span>
          </div>
          <p className="status-text" style={{ marginTop: '0.35rem' }}>
            Your landlord sent a renewal offer. Review and respond.
          </p>
          <div className="form-actions" style={{ marginTop: '0.5rem' }}>
            <a href="/tenant/lease#renewal-section" className="primary-button">View Renewal</a>
          </div>
        </div>
      )}

      <div className="subnav-wrap">
        <nav className="analytics-subnav" aria-label="Tenant dashboard sections">
          <button type="button" className={activeTab === 'new-items' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('new-items')}>New items</button>
          <button type="button" className={activeTab === 'viewings' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('viewings')}>Viewings</button>
          <button type="button" className={activeTab === 'applications' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('applications')}>Applications</button>
          <button type="button" className={activeTab === 'profile' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('profile')}>Profile</button>
        </nav>
      </div>

      {activeTab === 'new-items' && (
      <section className="dossier-section report-section new-items-card" style={{ borderLeft: '4px solid #2f6fed', background: '#f5f8ff' }}>
        <div className="section-header">
          <h3>New items</h3>
          <span className="report-chip report-chip--muted">Your activity</span>
        </div>
        <div className="kpi-grid" style={{ marginTop: '0.5rem' }}>
          {tenantSummaryItems.map((item) => (
            <div key={item.id} className={`kpi-card ${item.count === 0 ? 'muted' : ''}`}>
              <span>{item.label}</span>
              <strong>{item.count}</strong>
              <div className="form-actions" style={{ marginTop: '0.35rem' }}>
                <Link className="secondary-button" to={item.link}>View</Link>
              </div>
            </div>
          ))}
        </div>
      </section>
      )}

      {activeTab === 'viewings' && (
      <section className="dashboard-section" id="viewings" style={{ marginTop: '1rem' }}>
        <div className="section-header">
          <h3>Viewing Calendar</h3>
          <span className="report-chip report-chip--muted">Upcoming</span>
        </div>
        {upcomingViewingsCount === 0 ? (
          <div>
            <p className="status-text">No upcoming viewings scheduled.</p>
            <div className="form-actions" style={{ marginTop: '0.75rem' }}>
              <Link className="secondary-button" to="/properties">Browse listings</Link>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {upcomingBookings.map((vb) => (
              <div key={`vb-${vb.id}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.6rem 0.85rem', background: 'var(--surface-color, #f5f8ff)', borderRadius: '8px', border: '1px solid var(--border-color, #E6EEF2)', flexWrap: 'wrap', gap: '0.5rem' }}>
                <div>
                  <p style={{ margin: 0, fontWeight: 500, fontSize: '0.9rem', color: 'var(--text-color)' }}>
                    {vb.property_address}{vb.property_city ? `, ${vb.property_city}` : ''}
                  </p>
                  <p style={{ margin: '0.15rem 0 0', fontSize: '0.82rem', color: 'var(--light-text-color)' }}>
                    {new Date(vb.start_time).toLocaleString('en-CA', { timeZone: 'America/Toronto', dateStyle: 'full', timeStyle: 'short' })}
                  </p>
                </div>
                <button
                  className="secondary-button"
                  style={{ fontSize: '0.8rem', padding: '0.3rem 0.65rem' }}
                  onClick={async () => {
                    if (!window.confirm('Cancel this viewing?')) return;
                    setCancelBookingId(vb.id);
                    try {
                      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/viewings/${vb.id}/cancel`, {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${user.token}` }
                      });
                      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Failed'); }
                      setViewingBookings(prev => prev.map(b => b.id === vb.id ? { ...b, status: 'cancelled' } : b));
                    } catch (err) {
                      setError(err.message);
                    } finally {
                      setCancelBookingId(null);
                    }
                  }}
                  disabled={cancelBookingId === vb.id}
                >
                  {cancelBookingId === vb.id ? 'Cancelling...' : 'Cancel'}
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
      )}

      {activeTab === 'applications' && (
      <section className="dashboard-section" id="applications" style={{ marginTop: '1rem' }}>
        <div className="section-header">
          <h3>Your Applications</h3>
          <span className="report-chip report-chip--muted">{applicationCount} total</span>
        </div>
        {applications.length === 0 ? (
          <div>
            <p className="status-text">You haven't submitted any applications yet.</p>
            <div className="form-actions" style={{ marginTop: '0.75rem' }}>
              <Link className="secondary-button" to="/properties">Browse listings</Link>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {applications.map((app) => {
              const statusMap = {
                submitted: { label: 'Submitted', color: '#2563eb' },
                in_review: { label: 'In Review', color: '#7c3aed' },
                docs_requested: { label: 'Docs Requested', color: '#d97706' },
                info_needed: { label: 'In Review', color: '#7c3aed' },
                approved: { label: 'Approved', color: '#16a34a' },
                lease_drafted_landlord_signed: { label: 'Approved', color: '#16a34a' },
                lease_tenant_signed: { label: 'Tenant Signed', color: '#16a34a' },
                completed: { label: 'Completed', color: '#16a34a' },
                rejected: { label: 'Not Approved', color: '#dc2626' }
              };
              const s = statusMap[app.status] || { label: app.status, color: '#6b7280' };
              return (
                <Link
                  key={app.id}
                  to={`/application/${app.id}/documents`}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.6rem 0.85rem', background: 'var(--surface-color, #f5f8ff)', borderRadius: '8px', border: '1px solid var(--border-color, #E6EEF2)', textDecoration: 'none', color: 'inherit', flexWrap: 'wrap', gap: '0.4rem' }}
                >
                  <div style={{ minWidth: 0 }}>
                    <p style={{ margin: 0, fontWeight: 500, fontSize: '0.9rem', color: 'var(--text-color)' }}>
                      {app.property_address}{app.property_city ? `, ${app.property_city}` : ''}
                    </p>
                    <p style={{ margin: '0.15rem 0 0', fontSize: '0.78rem', color: 'var(--light-text-color)' }}>
                      Submitted {new Date(app.created_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                  <span className="status-pill" style={{ background: s.color, color: '#fff', fontSize: '0.72rem', padding: '0.2rem 0.5rem', borderRadius: '4px', whiteSpace: 'nowrap' }}>
                    {s.label}
                  </span>
                </Link>
              );
            })}
            <div className="form-actions" style={{ marginTop: '0.5rem' }}>
              <Link className="secondary-button" to="/tenant/applications">View all details</Link>
            </div>
          </div>
        )}
      </section>
      )}

      {activeTab === 'profile' && (
      <section className="dashboard-section" style={{ marginTop: '1rem' }}>
        <div className="section-header">
          <h3>Profile</h3>
          <span className="report-chip report-chip--muted">Used for applications</span>
        </div>
        {profileError && <p className="error-message">{profileError}</p>}
        {profileStatus && <p className="status-text">{profileStatus}</p>}
        <div className="ledger-grid" style={{ marginTop: '0.75rem' }}>
          <div className="ledger-item">
            <div className="form-group">
              <label>First name</label>
              <input name="first_name" className="form-control" value={profile.first_name || ''} onChange={handleProfileChange} />
            </div>
            <div className="form-group">
              <label>Last name</label>
              <input name="last_name" className="form-control" value={profile.last_name || ''} onChange={handleProfileChange} />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input name="phone" className="form-control" value={profile.phone || ''} onChange={handleProfileChange} />
            </div>
            <div className="form-group">
              <label>Date of birth</label>
              <input name="date_of_birth" type="date" className="form-control" value={(profile.date_of_birth || '').slice(0,10)} onChange={handleProfileChange} />
            </div>
          </div>
          <div className="ledger-item">
            <div className="form-group">
              <label>Address</label>
              <input name="address_line1" className="form-control" value={profile.address_line1 || ''} onChange={handleProfileChange} />
            </div>
            <div className="form-group">
              <label>City</label>
              <input name="city" className="form-control" value={profile.city || ''} onChange={handleProfileChange} />
            </div>
            <div className="form-group">
              <label>Province/State</label>
              <input name="province" className="form-control" value={profile.province || ''} onChange={handleProfileChange} />
            </div>
            <div className="form-group">
              <label>Postal code</label>
              <input name="postal_code" className="form-control" value={profile.postal_code || ''} onChange={handleProfileChange} />
            </div>
            <div className="form-group">
              <label>Country</label>
              <select name="country" className="form-control" value={profile.country || 'Canada'} onChange={handleProfileChange}>
                <option value="Canada">Canada</option>
              </select>
            </div>
          </div>
        </div>
        <button className="secondary-button" onClick={saveProfile} disabled={profileSaving}>
          {profileSaving ? 'Saving…' : 'Save profile'}
        </button>
      </section>
      )}

      {/* ── Recently Viewed Properties ── */}
      {recentlyViewed.length > 0 && (
      <section className="dashboard-section" style={{ marginTop: '1.5rem' }}>
        <div className="section-header">
          <h3>Recently viewed</h3>
          <Link className="secondary-button" to="/properties" style={{ fontSize: '0.8rem', padding: '0.25rem 0.6rem', textDecoration: 'none' }}>Browse all</Link>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem' }}>
          {recentlyViewed.map((prop) => (
            <Link
              key={prop.property_id}
              to={`/properties/${prop.property_id}`}
              style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0.65rem', background: 'var(--surface-color, #f5f8ff)', borderRadius: '8px', border: '1px solid var(--border-color, #E6EEF2)', textDecoration: 'none', color: 'inherit' }}
            >
              {prop.image_url ? (
                <img
                  src={prop.image_url}
                  alt=""
                  style={{ width: '52px', height: '52px', borderRadius: '6px', objectFit: 'cover', flexShrink: 0 }}
                />
              ) : (
                <div style={{ width: '52px', height: '52px', borderRadius: '6px', background: 'var(--border-color, #E6EEF2)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }}>🏠</div>
              )}
              <div style={{ minWidth: 0 }}>
                <p style={{ margin: 0, fontWeight: 500, fontSize: '0.88rem', color: 'var(--text-color)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {prop.address}{prop.city ? `, ${prop.city}` : ''}
                </p>
                <p style={{ margin: '0.1rem 0 0', fontSize: '0.78rem', color: 'var(--light-text-color)' }}>
                  {prop.rent_amount ? `$${Number(prop.rent_amount).toLocaleString()}/mo` : ''}
                  {prop.rent_amount && prop.viewed_at ? ' · ' : ''}
                  {prop.viewed_at ? `Viewed ${new Date(prop.viewed_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}` : ''}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>
      )}

      {/* ── Data & Privacy ── */}
      <section className="dashboard-section" style={{ marginTop: '2rem' }}>
        <div className="section-header">
          <h3>Data & Privacy</h3>
        </div>
        <p className="status-text" style={{ marginBottom: '0.75rem' }}>
          We retain your application documents for a limited time and delete them automatically.
          You can request full deletion of your data at any time.
        </p>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button
            className="ghost-button"
            style={{ fontSize: '0.85rem' }}
            onClick={async () => {
              try {
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/data-retention/policy`);
                const data = await res.json();
                const lines = Object.entries(data.policy).map(([, v]) => `${v.description}: ${v.days} days`).join('\n');
                alert('Data Retention Policy:\n\n' + lines + '\n\nContact: ' + data.contact);
              } catch { alert('Could not load policy. Contact support@rentual.co'); }
            }}
          >View Retention Policy</button>
          <button
            className="ghost-button"
            style={{ fontSize: '0.85rem', color: '#ef4444', borderColor: '#ef4444' }}
            onClick={async () => {
              if (!window.confirm('Are you sure you want to delete all your data? This action cannot be undone.\n\nThis will delete:\n- All applications and documents\n- Screening reports\n- Your account information\n\nActive leases must be closed first.')) return;
              if (!window.confirm('This is permanent. Type "delete" in the next prompt to confirm.')) return;
              const confirmation = window.prompt('Type "delete" to confirm:');
              if (confirmation?.toLowerCase() !== 'delete') return;
              try {
                const token = localStorage.getItem('token');
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/data-retention/deletion-request`, {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
                });
                const data = await res.json();
                if (res.ok) {
                  alert('Your data has been deleted. You will be logged out.');
                  localStorage.removeItem('token');
                  window.location.href = '/';
                } else {
                  alert(data.error || 'Deletion failed. Contact support@rentual.co');
                }
              } catch { alert('Request failed. Contact support@rentual.co'); }
            }}
          >Delete My Data</button>
        </div>
      </section>
    </main>
  );
}

export default TenantDashboard;
